import { ProductParsingError, UnsupportedProduct } from './errors';
const KNOWN_PRODUCT_NAME_BASE = ['DC', 'KT', 'BAL'];
export class Product {
    constructor(args) {
        this.logger = Product.logger;
        this.nameBase = args.nameBase;
        this.major = args.major;
        this.minor = args.minor;
        this.patch = args.patch;
    }
    /**
     * Set static options like the logger for the Product class.
     */
    static setGlobalOptions(options) {
        if (options.logger) {
            Product.logger = options.logger;
        }
    }
    static isKnownProductNameBase(productNameBase) {
        return KNOWN_PRODUCT_NAME_BASE.includes(productNameBase);
    }
    static parseNameBase(productName) {
        // Split the product name into the base and version, base splitting before the first number
        return productName.split(/[0-9]/)[0];
    }
    static parseFromParts(nameBase, version) {
        return Product.parse(`${nameBase}${version}`);
    }
    /**
     * Parses a product name into a Product. Returns an error if it fails to parse the product name.
     */
    static parse(productName) {
        if (typeof productName !== 'string') {
            return {
                product: null,
                error: new UnsupportedProduct(productName),
            };
        }
        const nameBase = Product.parseNameBase(productName);
        if (!nameBase) {
            return {
                product: null,
                error: new ProductParsingError(productName, 'Failed to parse product name base'),
            };
        }
        const version = productName.split(nameBase)[1];
        // Check that version exists and its only numbers and dots
        if (!version || !/^[0-9.]*$/.test(version)) {
            return {
                product: null,
                error: new ProductParsingError(productName, 'Failed to parse product version'),
            };
        }
        const versionArr = version === null || version === void 0 ? void 0 : version.split('.').map(Number);
        if (!versionArr || versionArr.length < 1 || versionArr.length > 3) {
            return {
                product: null,
                error: new ProductParsingError(productName, 'Failed to parse product version into major minor and patch'),
            };
        }
        const product = new Product({
            nameBase,
            major: versionArr[0],
            minor: versionArr[1],
            patch: versionArr[2],
        });
        return {
            product: product,
            error: null,
        };
    }
    static isPlasma(productName) {
        if (!productName) {
            return false;
        }
        const { product, error } = Product.parse(productName);
        if (error) {
            this.logger.warn({ err: error }, 'Error parsing product');
            return false;
        }
        return product.isPlasma();
    }
    static isBal(productName) {
        if (!productName) {
            return false;
        }
        const { product, error } = Product.parse(productName);
        if (error) {
            this.logger.warn({ err: error }, 'Error parsing product');
            return false;
        }
        return product.isBal();
    }
    is(productNameBase) {
        return this.nameBase === productNameBase;
    }
    isBal() {
        return this.is('BAL');
    }
    isPlasma() {
        return this.is('DC') || this.is('KT');
    }
    lt(compare) {
        var _a, _b, _c, _d;
        const { product, sameNameBase } = this.parseAndAssertSameProduct(compare);
        if (!sameNameBase) {
            return false;
        }
        if (this.major !== product.getMajor()) {
            return this.major < product.getMajor();
        }
        const thisMinor = (_a = this.minor) !== null && _a !== void 0 ? _a : 0;
        const compareMinor = (_b = product.getMinor()) !== null && _b !== void 0 ? _b : 0;
        if (thisMinor !== compareMinor) {
            return thisMinor < compareMinor;
        }
        const thisPatch = (_c = this.patch) !== null && _c !== void 0 ? _c : 0;
        const comparePatch = (_d = product.getPatch()) !== null && _d !== void 0 ? _d : 0;
        return thisPatch < comparePatch;
    }
    gte(compare) {
        var _a, _b, _c, _d;
        const { product, sameNameBase } = this.parseAndAssertSameProduct(compare);
        if (!sameNameBase) {
            return false;
        }
        if (this.major !== product.getMajor()) {
            return this.major >= product.getMajor();
        }
        const thisMinor = (_a = this.minor) !== null && _a !== void 0 ? _a : 0;
        const compareMinor = (_b = product.getMinor()) !== null && _b !== void 0 ? _b : 0;
        if (thisMinor !== compareMinor) {
            return thisMinor >= compareMinor;
        }
        const thisPatch = (_c = this.patch) !== null && _c !== void 0 ? _c : 0;
        const comparePatch = (_d = product.getPatch()) !== null && _d !== void 0 ? _d : 0;
        return thisPatch >= comparePatch;
    }
    toName() {
        return `${this.nameBase}${[this.major, this.minor, this.patch].filter((v) => v != null).join('.')}`;
    }
    getNameBase() {
        return this.nameBase;
    }
    getMajor() {
        return this.major;
    }
    getMinor() {
        return this.minor;
    }
    getPatch() {
        return this.patch;
    }
    parseAndAssertSameProduct(product) {
        const { product: parsedProduct, error } = product instanceof Product ? { product, error: null } : Product.parse(product);
        if (error) {
            this.logger.warn({ err: error }, 'Error parsing product');
            return {
                product: null,
                sameNameBase: false,
            };
        }
        if (this.nameBase === parsedProduct.nameBase) {
            return {
                product: parsedProduct,
                sameNameBase: true,
            };
        }
        return {
            product: null,
            sameNameBase: false,
        };
    }
}
Product.logger = console;
